import React, { useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ticketsApi } from '@/api/tickets.api';
import { usersApi } from '@/api/users.api';
import { attachmentsApi } from '@/api/shopify.api';
import { LoadingState } from '@/components/ui/LoadingState';
import { ErrorState } from '@/components/ui/ErrorState';
import {
  ApprovalBadge,
  BreachBadge,
  CxBadge,
  SeverityBadge
} from '@/components/tickets/badges';
import { StatusPipeline } from '@/components/tickets/StatusPipeline';
import { ChangeStatusModal } from '@/components/tickets/actions/ChangeStatusModal';
import { ChangeCategoryModal } from '@/components/tickets/actions/ChangeCategoryModal';
import { LogCxModal } from '@/components/tickets/actions/LogCxModal';
import { CloseTicketModal } from '@/components/tickets/actions/CloseTicketModal';
import { AddNoteModal } from '@/components/tickets/actions/AddNoteModal';
import { ResolutionModal } from '@/components/tickets/actions/ResolutionModal';
import { AttachmentPreviewModal } from '@/components/tickets/AttachmentPreviewModal';
import { HandoffModal } from '@/components/tickets/actions/HandoffModal';
import { ReassignModal } from '@/components/tickets/actions/ReassignModal';
import { ApproveModal } from '@/components/tickets/actions/ApproveModal';
import { IssueFaultModal } from '@/components/tickets/actions/IssueFaultModal';
import { faultApi } from '@/api/fault.api';
import { useAuth } from '@/context/AuthContext';
import {
  ApprovalState,
  TicketStatus,
  ViewType,
  FAULT_ATTRIBUTION_VALUES,
  FAULT_ATTRIBUTION_LABELS,
  FaultAttribution,
  canLogCx,
  categoryUsesCx,
  formatCost
} from '@/types/domain';
import { formatBytes, formatDateTime, formatRelative } from '@/lib/format';
import { categoryLabel, storeLabel } from '@/lib/labels';
import { useRealtime } from '@/context/RealtimeContext';
import { useToasts } from '@/context/ToastContext';
import { ApiError } from '@/lib/errors';

/**
 * V4.9.2+ — Render a string with URLs auto-linked. Detects http://, https://,
 * www., and bare domains. Used in timeline event descriptions and notes so
 * pasted Google Drive / Shopify / Slack links become clickable instead of
 * staying as plain text.
 *
 * Safe-by-default: only converts http(s)://… into <a target="_blank"
 * rel="noopener noreferrer">. The rest is rendered as plain text — no HTML
 * is parsed from user input, so this can't be used for XSS.
 */
/**
 * V4.9.10 — Map a timeline event's actor to a role-on-this-ticket CSS
 * class so the entry can be color-coded. Solver/Communicator/Owner are
 * derived from the ticket's assignment; admin is detected via a flag
 * on the actor; everything else (raised-by, system) gets a fallback.
 *
 * Returns one of: role-solver, role-communicator, role-owner,
 * role-admin, role-raised-by, role-system.
 */
function deriveActorRoleClass(
  actorId: string | null | undefined,
  ticket: { solver?: { id: string } | null; communicator?: { id: string } | null; owner?: { id: string } | null; raised_by?: { id: string } | null }
): string {
  if (!actorId) return 'role-system';
  // Idris (Admin) is always assignable. We match by name pattern since
  // we don't have a direct flag on TimelineEvent — but the ticket
  // relations only give us IDs, so we approximate using the user lookup.
  // For simple cases: actor === solver → solver; etc.
  if (ticket.solver?.id === actorId) return 'role-solver';
  if (ticket.communicator?.id === actorId) return 'role-communicator';
  if (ticket.owner?.id === actorId) return 'role-owner';
  if (ticket.raised_by?.id === actorId) return 'role-raised-by';
  // Fallback — could be Admin or some other actor (e.g. SLA-warning job).
  return 'role-admin';
}

function renderLinkified(text: string | null | undefined): React.ReactNode {
  if (!text) return null;
  // V4.9.5 — unified pattern matches both URLs and @-mention tokens.
  // Capture group 1 = URL,  group 2 = mention alias (without @).
  // The `(?<!\S)` ensures @-mentions don't match emails (name@domain.com).
  const re = /(https?:\/\/[^\s<>"']+)|(?:(?<!\S)@([a-zA-Z][a-zA-Z0-9_.-]*))/g;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;
  while ((match = re.exec(text)) !== null) {
    const before = text.slice(lastIndex, match.index);
    if (before) parts.push(<span key={`t${key++}`}>{before}</span>);
    if (match[1]) {
      // URL match
      const url = match[1];
      const trimmed = url.replace(/[.,;:!?)\]}]+$/, '');
      const trailingPunct = url.slice(trimmed.length);
      parts.push(
        <a
          key={`a${key++}`}
          href={trimmed}
          target="_blank"
          rel="noopener noreferrer"
          className="timeline-link"
        >
          {trimmed}
        </a>
      );
      if (trailingPunct) parts.push(<span key={`t${key++}`}>{trailingPunct}</span>);
      lastIndex = match.index + url.length;
    } else if (match[2]) {
      // @-mention match
      const alias = match[2];
      parts.push(
        <span key={`m${key++}`} className="mention-token">
          @{alias}
        </span>
      );
      lastIndex = match.index + alias.length + 1; // +1 for the '@'
    }
  }
  const tail = text.slice(lastIndex);
  if (tail) parts.push(<span key={`t${key++}`}>{tail}</span>);
  return <>{parts}</>;
}

export function TicketDetailPage() {
  const { id } = useParams<{ id: string }>();
  const ticketId = id ?? '';
  const navigate = useNavigate();
  // V4.9.3 — used by the origin-aware Back button below.
  const location = useLocation();
  const { user, hasCapability } = useAuth();
  const { on } = useRealtime();
  const queryClient = useQueryClient();
  const { push } = useToasts();

  const [showStatus, setShowStatus] = useState(false);
  const [showCx, setShowCx] = useState(false);
  const [showClose, setShowClose] = useState(false);
  const [showNote, setShowNote] = useState(false);
  const [showHandoff, setShowHandoff] = useState(false);
  const [showReassign, setShowReassign] = useState(false);
  const [showApprove, setShowApprove] = useState(false);
  const [showCategory, setShowCategory] = useState(false);
  // V4.9.19 — manual multi-party fault panel.
  const [showFault, setShowFault] = useState(false);
  // V4.9.26 — solver "Resolution details" editor (top card).
  const [showResolution, setShowResolution] = useState(false);
  // V4.9.7 — Preview modal state. Holds the attachment being viewed so the
  // modal can stream + render it without forcing a download.
  const [previewAttachment, setPreviewAttachment] = useState<{
    id: string;
    s3Key: string;
    filename: string;
    mimeType: string | null;
    sizeBytes: string | null;
  } | null>(null);

  const {
    data: ticket,
    isLoading,
    isError,
    error,
    refetch,
    isFetching
  } = useQuery({
    queryKey: ['ticket', ticketId],
    queryFn: () => ticketsApi.getById(ticketId),
    enabled: ticketId.length > 0,
    // V4.9.19 — the status pipeline / badges read straight off this query, so
    // they must never show a stale stage. Always refetch when the page mounts
    // or regains focus (in addition to the post-action invalidation + realtime
    // pushes), so "I changed the status" is reflected above immediately and a
    // returning tab never lingers on an old step.
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    staleTime: 0
  });

  // V3 fix — load users so we can replace raw UUIDs (investigator_id,
  // solver_id, raised_by_id) with human aliases in the Team panel.
  const usersQuery = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.list(),
    staleTime: 60_000
  });
  const userById = React.useMemo(() => {
    const map = new Map<string, { name: string; alias: string }>();
    for (const u of usersQuery.data ?? []) {
      map.set(u.id, { name: u.name, alias: u.alias });
    }
    return map;
  }, [usersQuery.data]);

  // V4.9.19 — current manual fault marks for this ticket (shown in the band +
  // pre-fills the Fault modal). Read-only for everyone; only Admin/Solver edit.
  const faultMarksQuery = useQuery({
    queryKey: ['fault-marks', ticketId],
    queryFn: () => faultApi.getMarks(ticketId),
    enabled: ticketId.length > 0
  });
  const aliasFor = React.useCallback(
    (id: string | null | undefined): string => {
      if (!id) return '—';
      const u = userById.get(id);
      return u ? `${u.name} (${u.alias})` : id;
    },
    [userById]
  );

  React.useEffect(() => {
    const off1 = on('ticket.status_changed', (data) => {
      if ((data.ticket_id as string) === ticketId) {
        void queryClient.invalidateQueries({ queryKey: ['ticket', ticketId] });
      }
    });
    const off2 = on('ticket.updated', (data) => {
      if ((data.ticket_id as string) === ticketId) {
        void queryClient.invalidateQueries({ queryKey: ['ticket', ticketId] });
      }
    });
    const off3 = on('ticket.approval_decided', (data) => {
      if ((data.ticket_id as string) === ticketId) {
        void queryClient.invalidateQueries({ queryKey: ['ticket', ticketId] });
      }
    });
    const off4 = on('ticket.cx_informed', (data) => {
      if ((data.ticket_id as string) === ticketId) {
        void queryClient.invalidateQueries({ queryKey: ['ticket', ticketId] });
      }
    });
    return () => {
      off1();
      off2();
      off3();
      off4();
    };
  }, [on, ticketId, queryClient]);

  const deleteMutation = useMutation({
    mutationFn: () => ticketsApi.remove(ticketId),
    onSuccess: () => {
      push({ kind: 'success', title: 'Ticket deleted' });
      void queryClient.invalidateQueries({ queryKey: ['tickets'] });
      navigate('/tickets');
    },
    onError: (err: unknown) => {
      push({
        kind: 'error',
        title: 'Delete failed',
        message: err instanceof ApiError ? err.problem?.detail ?? err.message : undefined
      });
    }
  });

  /**
   * V4.9.10 — Admin sets the fault attribution. Optimistically updates
   * the cached TicketDetail so the dropdown reflects the new value
   * instantly; the timeline event reload (via the realtime channel +
   * cache invalidation) catches it server-side too.
   */
  const faultMutation = useMutation({
    mutationFn: (fault: string | null) => ticketsApi.setFault(ticketId, fault),
    onSuccess: (data) => {
      queryClient.setQueryData(['ticket', ticketId], data);
      void queryClient.invalidateQueries({ queryKey: ['ticket', ticketId] });
      void queryClient.invalidateQueries({ queryKey: ['tickets'] });
      void queryClient.invalidateQueries({ queryKey: ['reports'] });
      push({ kind: 'success', title: 'Fault attribution updated' });
    },
    onError: (err: unknown) => {
      push({
        kind: 'error',
        title: 'Could not update fault attribution',
        message: err instanceof ApiError ? err.problem?.detail ?? err.message : undefined
      });
    }
  });

  if (!ticketId) return <ErrorState error={new Error('Invalid ticket id')} />;
  if (isLoading) return <LoadingState label="Loading ticket..." large />;
  if (isError || !ticket) return <ErrorState error={error ?? new Error('Ticket not found')} onRetry={() => refetch()} />;

  const isAdmin = user?.view_type === ViewType.Admin;
  const isOwner = user ? [ticket.owner_id, ticket.solver_id, ticket.investigator_id].includes(user.sub) : false;
  // V4.3 — gate per-action buttons on the same capabilities the backend
  // checks, so a Specialist or Communicator the admin granted "tickets.close"
  // (or "tickets.delete") via the matrix actually sees the button. V4.2 hard-
  // coded these to view_type which made the Permissions matrix half-broken
  // for non-admin roles.
  const canModify = !!user && (
    hasCapability('tickets.update_status') ||
    hasCapability('tickets.log_cx') ||
    hasCapability('tickets.add_note') ||
    hasCapability('tickets.handoff') ||
    hasCapability('tickets.reassign')
  );
  const canClose =
    hasCapability('tickets.close') &&
    [TicketStatus.Open, TicketStatus.InProgress, TicketStatus.WaitingApproval].includes(ticket.status) &&
    [ApprovalState.NotNeeded, ApprovalState.Approved].includes(ticket.approval_state);
  const canDelete = hasCapability('tickets.delete');
  // V4.9.5 — Approval is HARD-LOCKED to Admin role on the backend (see
  // tickets.service.ts processApproval). Hide the button from non-admins
  // even if they somehow have the tickets.approve capability granted —
  // it would just 403 when clicked.
  const canApprove = user?.view_type === 'Admin' && hasCapability('tickets.approve');
  const needsApproval = ticket.approval_state === ApprovalState.WaitingAdmin;
  // V4.9.19 — manual fault marking is for an Admin (any ticket) or the
  // ticket's assigned Solver (their own ticket). Enforced again server-side.
  const canMarkFault = !!user && (isAdmin || ticket.solver_id === user.sub);
  const faultMarks = faultMarksQuery.data;

  return (
    <div>
      <div className="section-header">
        <div>
          {(() => {
            // V4.9.3 — origin-aware Back link. Reads location.state.from set
            // by TicketTable so the Back button returns to the list page the
            // user came from (My Queue, Approvals, Archives, etc.) instead of
            // always dumping them on All Tickets.
            //
            // V4.9.5+ BUGFIX — `from` now includes the query string (sort,
            // filters, page) so the back link restores the EXACT view the
            // user was looking at. Lookup logic strips the query to find
            // the right label, but the navigation target keeps it.
            const BACK_LABELS: Record<string, string> = {
              '/tickets': 'All Tickets',
              '/my-queue': 'My Queue',
              '/approvals': 'Approvals',
              '/archives': 'Archives',
              '/deleted': 'Deleted',
              '/dashboard': 'Dashboard',
              '/my-tags': 'My Tags'
            };
            const navState = (location.state as {
              from?: string;
              ticketIds?: string[];
            } | null) ?? {};
            const from = navState.from;
            // Strip query string when looking up the label, but keep it on
            // the actual navigation target so filters/sort/page round-trip.
            const fromPath = from ? from.split('?')[0] : null;
            const backTo = fromPath && BACK_LABELS[fromPath] ? from! : '/tickets';
            const backLabel =
              (fromPath && BACK_LABELS[fromPath]) || BACK_LABELS['/tickets'] || 'tickets';

            // V4.9.5 — Prev / Next arrow navigation through the list the
            // user came from. The list of ticket IDs (in visible order) is
            // passed in router state by TicketTable. Arrows reuse the
            // same `from` + `ticketIds` state so chained Next clicks keep
            // moving forward through the list, and hitting Back still
            // lands on the original list page.
            const ticketIds = Array.isArray(navState.ticketIds) ? navState.ticketIds : null;
            const idx = ticketIds ? ticketIds.indexOf(ticket.id) : -1;
            const prevId = idx > 0 ? ticketIds![idx - 1] : null;
            const nextId = idx >= 0 && ticketIds && idx < ticketIds.length - 1
              ? ticketIds[idx + 1]
              : null;
            const goTo = (id: string) =>
              navigate(`/tickets/${id}`, {
                state: { from, ticketIds },
                replace: false
              });

            return (
              <div className="ticket-nav-row">
                <Link to={backTo} state={{ from }} className="text-small">
                  ← Back to {backLabel}
                </Link>
                {ticketIds && ticketIds.length > 1 ? (
                  <div className="ticket-nav-arrows">
                    <button
                      type="button"
                      className="ticket-nav-arrow"
                      title={prevId ? `Previous: ${prevId}` : 'No previous ticket'}
                      disabled={!prevId}
                      onClick={() => prevId && goTo(prevId)}
                      aria-label="Previous ticket"
                    >
                      ←
                    </button>
                    <span className="ticket-nav-position">
                      {idx + 1} / {ticketIds.length}
                    </span>
                    <button
                      type="button"
                      className="ticket-nav-arrow"
                      title={nextId ? `Next: ${nextId}` : 'No next ticket'}
                      disabled={!nextId}
                      onClick={() => nextId && goTo(nextId)}
                      aria-label="Next ticket"
                    >
                      →
                    </button>
                  </div>
                ) : null}
              </div>
            );
          })()}
          {/* V4.9.10 — Archived banner. When a ticket is archived but
              someone clicks the digest email link, the page still loads
              (backend accepts archived) but shows this banner so the
              user knows the state and can unarchive if needed. */}
          {ticket.archived_at ? (
            <div
              style={{
                marginBottom: 12,
                padding: '8px 14px',
                background: '#fef3c7',
                border: '1px solid #fcd34d',
                borderRadius: 8,
                color: '#92400e',
                fontSize: 13
              }}
            >
              <strong>This ticket is archived.</strong> It was moved to Archives on{' '}
              {new Date(ticket.archived_at).toLocaleDateString('en-IN', {
                day: 'numeric',
                month: 'short',
                year: 'numeric'
              })}
              . You can still view all its details, and admin can unarchive it from the Archives page.
            </div>
          ) : null}

          {/* V4.9.13 — Top header leads with just the ID + the chevron stage
              pipeline + NEXT ACTION (below). The status meta badges (severity,
              SLA/breach, CX, approval, changed-by) moved to the "Status" card
              in the right column — see the <aside> further down. */}
          <h2 className="ticket-header-badges">
            <span className="font-mono">{ticket.id}</span>
          </h2>
          {/* V4.9.13 — Chevron stage pipeline (hero). Shows where the ticket
              sits in its category's journey AND whose turn it is, replacing
              the old lone status badge + the "AWAITED BY" pill. The current
              stage is highlighted; earlier stages read as done, later as
              upcoming. */}
          <StatusPipeline
            category={ticket.category}
            status={ticket.status}
            awaited={ticket.action_required_by}
          />
        </div>
        <div className="ticket-action-bar">
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => window.location.reload()}
            disabled={isFetching}
          >
            {isFetching ? 'Refreshing...' : 'Refresh'}
          </button>
          {canModify ? (
            <>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowStatus(true)}>
                Change status
              </button>
              {/* V4.9.3 — Change category mid-flight (e.g. Replacement → Refund) */}
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowCategory(true)}>
                Change category
              </button>
              {/* V4.9.13 — Log CX is only offered at the customer-update
                  stages defined per category (e.g. A2 "Inform Customer",
                  A3/A4 "…Shipped", A6 "Call Carrier"). Hidden everywhere
                  else so the button only appears when it's actually the
                  right moment to log a customer communication. */}
              {canLogCx(ticket.category, ticket.status) ? (
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowCx(true)}>
                  Log CX
                </button>
              ) : null}
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowNote(true)}>
                Add note
              </button>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setShowHandoff(true)}
              >
                Handoff
              </button>
            </>
          ) : null}
          {canClose ? (
            <button type="button" className="btn btn-primary btn-sm" onClick={() => setShowClose(true)}>
              Close ticket
            </button>
          ) : null}
          {canModify ? (
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => setShowReassign(true)}
            >
              Reassign
            </button>
          ) : null}
          {/* V4.9.19 — Mark fault (Admin or this ticket's Solver). Opens the
              multi-party fault panel. */}
          {canMarkFault ? (
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => setShowFault(true)}
              title="Mark which people / teams are at fault for this ticket (feeds Reports)."
            >
              Fault
            </button>
          ) : null}
          {needsApproval && canApprove ? (
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => setShowApprove(true)}
            >
              Decide approval
            </button>
          ) : null}
          {canDelete ? (
            <button
              type="button"
              className="btn btn-danger btn-sm"
              onClick={() => {
                if (window.confirm('Delete this ticket? This is a soft delete.')) {
                  deleteMutation.mutate();
                }
              }}
              disabled={deleteMutation.isPending}
            >
              Delete
            </button>
          ) : null}
        </div>
      </div>

      {/* V4.9.26 — Solver "Resolution details", pinned at the TOP and editable
          anytime (independent of notes). Always visible so it's never a hurdle
          and the solver can fill/update it whenever. */}
      {(() => {
        const costStr = formatCost(ticket.cost_region, ticket.cost_amount);
        const hasAny =
          !!ticket.issue_note || !!costStr || !!ticket.payment_mode || !!ticket.remarks;
        return (
          <div className="card resolution-top-card">
            <div
              className="card-header"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
            >
              <h3 className="card-title" style={{ margin: 0 }}>Resolution details</h3>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setShowResolution(true)}
              >
                {hasAny ? 'Update' : 'Add details'}
              </button>
            </div>
            <div className="card-body">
              {hasAny ? (
                <div className="resolution-layout">
                  {/* Money facts as prominent tiles. */}
                  {costStr || ticket.payment_mode ? (
                    <div className="resolution-tiles">
                      <div className="resolution-tile">
                        <span className="resolution-tile-label">Cost</span>
                        {costStr ? (
                          <>
                            <span className="resolution-tile-value resolution-cost">{costStr}</span>
                            <span className="resolution-tile-sub">{ticket.cost_region}</span>
                          </>
                        ) : (
                          <span className="resolution-tile-empty">—</span>
                        )}
                      </div>
                      <div className="resolution-tile">
                        <span className="resolution-tile-label">Payment mode</span>
                        {ticket.payment_mode ? (
                          <span className="resolution-tile-value">{ticket.payment_mode}</span>
                        ) : (
                          <span className="resolution-tile-empty">—</span>
                        )}
                      </div>
                    </div>
                  ) : null}

                  {/* Narrative fields as labelled blocks. */}
                  {ticket.issue_note || ticket.remarks ? (
                    <div className="resolution-texts">
                      {ticket.issue_note ? (
                        <div className="resolution-text">
                          <span className="resolution-text-label">Issues</span>
                          <p className="resolution-text-body">{ticket.issue_note}</p>
                        </div>
                      ) : null}
                      {ticket.remarks ? (
                        <div className="resolution-text">
                          <span className="resolution-text-label">Remarks</span>
                          <p className="resolution-text-body">{ticket.remarks}</p>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              ) : (
                <div className="resolution-empty">
                  <span className="resolution-empty-icon" aria-hidden="true">🧾</span>
                  <p className="resolution-empty-text">
                    No resolution details yet. Record the <strong>issue</strong>, <strong>cost</strong>,{' '}
                    <strong>payment mode</strong> and <strong>remarks</strong> — you can update them anytime.
                  </p>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={() => setShowResolution(true)}
                  >
                    Add details
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      <div className="detail-grid">
        <div className="flex flex-col gap-4">
          <div className="card">
            <div className="card-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <h3 className="card-title" style={{ margin: 0 }}>Summary</h3>
                {/* V4.7 — show who wrote the summary. Falls back to the
                    actor of the "created" timeline event when the backend
                    didn't return a raised_by relation (older tickets). */}
                {(() => {
                  const author =
                    ticket.raised_by?.name ||
                    ticket.timeline_events.find((e) => e.eventType === 'created')?.actorName ||
                    null;
                  return author ? (
                    <span className="summary-author" title="Person who wrote / raised this ticket">
                      Written by <span className="summary-author-name">{author}</span>
                    </span>
                  ) : null;
                })()}
              </div>
              {ticket.shopify_admin_url ? (
                <a
                  className="btn btn-link"
                  href={ticket.shopify_admin_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View in Shopify →
                </a>
              ) : null}
            </div>
            <div className="card-body">
              <p style={{ marginTop: 0 }}>{ticket.summary}</p>
              {ticket.requested_action ? (
                <>
                  <h4 className="text-small text-muted">Requested action</h4>
                  <p>{ticket.requested_action}</p>
                </>
              ) : null}
              {ticket.handoff_note ? (
                <>
                  <h4 className="text-small text-muted">Handoff note</h4>
                  <p>{ticket.handoff_note}</p>
                </>
              ) : null}
              {isAdmin && ticket.private_notes ? (
                <>
                  <h4 className="text-small text-muted">Admin private notes</h4>
                  <pre
                    style={{
                      background: '#f8fafc',
                      padding: 12,
                      borderRadius: 8,
                      whiteSpace: 'pre-wrap',
                      fontFamily: 'inherit'
                    }}
                  >
                    {ticket.private_notes}
                  </pre>
                </>
              ) : null}
            </div>
          </div>

          {/* V4.9.19 — Manual fault band: who a Solver/Admin marked responsible
              for this ticket. Read-only for everyone; Admin/assigned-Solver can
              edit. Independent of the AI fault attribution. */}
          <div className="card">
            <div
              className="card-header"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
            >
              <h3 className="card-title" style={{ margin: 0 }}>Fault — who’s responsible</h3>
              {canMarkFault ? (
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowFault(true)}>
                  {faultMarks && faultMarks.total > 0 ? 'Edit fault' : 'Mark fault'}
                </button>
              ) : null}
            </div>
            <div className="card-body">
              {faultMarksQuery.isLoading ? (
                <div className="text-muted text-small">Loading…</div>
              ) : faultMarks && faultMarks.total > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {(
                    [
                      ['sales', 'Sales'],
                      ['designers', 'Designers'],
                      ['shipment', 'Shipment'],
                      ['vendors', 'Vendors']
                    ] as const
                  ).map(([key, label]) => {
                    const people = faultMarks.teams[key] ?? [];
                    if (!people.length) return null;
                    return (
                      <div key={key} style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                        <span className="text-small" style={{ fontWeight: 700, minWidth: 78 }}>{label}:</span>
                        {people.map((p, i) => (
                          <span
                            key={`${key}-${i}`}
                            style={{
                              display: 'inline-block',
                              padding: '2px 9px',
                              borderRadius: 999,
                              background: '#fee2e2',
                              color: '#991b1b',
                              fontSize: 12,
                              fontWeight: 600
                            }}
                          >
                            {p.name}
                          </span>
                        ))}
                      </div>
                    );
                  })}
                  {faultMarks.customer ? (
                    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                      <span className="text-small" style={{ fontWeight: 700, minWidth: 78 }}>Customer:</span>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '2px 9px',
                          borderRadius: 999,
                          background: '#fef3c7',
                          color: '#92400e',
                          fontSize: 12,
                          fontWeight: 600
                        }}
                      >
                        {faultMarks.customer_name || 'Customer'}
                      </span>
                    </div>
                  ) : null}
                  {faultMarks.updated_by ? (
                    <div className="text-small text-muted" style={{ marginTop: 4 }}>
                      Marked by {faultMarks.updated_by}
                      {faultMarks.updated_at ? ` · ${formatRelative(faultMarks.updated_at)}` : ''}
                    </div>
                  ) : null}
                </div>
              ) : (
                <div className="text-muted text-small">
                  No one marked at fault yet.
                  {canMarkFault ? ' Use “Mark fault” to record who’s responsible.' : ''}
                </div>
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Timeline</h3>
            </div>
            <div className="card-body">
              {ticket.timeline_events.length === 0 ? (
                <div className="text-muted">No events yet</div>
              ) : (
                <>
                  {/* V4.9.10 — Color-by-role legend (Item #3). Tells the
                     reader what the colored left band on each entry means. */}
                  <div className="timeline-legend">
                    <span className="timeline-legend-item role-solver">Solver</span>
                    <span className="timeline-legend-item role-communicator">Communicator</span>
                    <span className="timeline-legend-item role-owner">Owner</span>
                    <span className="timeline-legend-item role-admin">Admin</span>
                    <span className="timeline-legend-item role-raised-by">Customer / Raised by</span>
                    <span className="timeline-legend-item role-system">System</span>
                  </div>
                  <div className="timeline">
                    {ticket.timeline_events.map((event) => {
                      const safeType = event.eventType.replace(/\s+/g, '-');
                      const meta = event.metadata as Record<string, unknown> | null;
                      const noteFromMeta =
                        meta && typeof meta.note === 'string' && meta.note.trim().length > 0
                          ? meta.note
                          : null;
                      // V4.9.10 — derive the actor's role ON THIS TICKET so we
                      // can color the timeline entry. Solver → blue, Comm →
                      // green, Owner → amber, Admin → purple, customer → grey,
                      // system (no actor) → dark grey.
                      const roleClass = deriveActorRoleClass(event.actorId, ticket);
                      return (
                        <div
                          key={event.id}
                          className={`timeline-item event-${safeType} ${roleClass} ${event.actorId ? '' : 'system'}`}
                        >
                          <div className="timeline-item-title">
                            <span className={`timeline-badge badge-${safeType}`}>
                              {event.eventType.replace(/_/g, ' ')}
                            </span>
                          </div>
                          <div className="timeline-item-description">
                            {renderLinkified(event.description)}
                          </div>
                          {noteFromMeta ? (
                            <div className="timeline-item-note">
                              <span className="timeline-item-note-label">Note:</span>{' '}
                              {renderLinkified(noteFromMeta)}
                            </div>
                          ) : null}
                          <div className="timeline-item-meta">
                            {event.actorName} · {formatRelative(event.createdAt)} ·{' '}
                            {formatDateTime(event.createdAt)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h3 className="card-title">CX communications</h3>
            </div>
            <div className="card-body">
              {ticket.cx_communications.length === 0 ? (
                <div className="text-muted">No CX communications logged</div>
              ) : (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Method</th>
                      <th>Note</th>
                      <th>When</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ticket.cx_communications.map((cx) => (
                      <tr key={cx.id} className="no-hover">
                        <td>{cx.method}</td>
                        <td>{cx.note ?? '—'}</td>
                        <td>{formatDateTime(cx.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {ticket.attachments.length > 0 ? (
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Attachments</h3>
              </div>
              <div className="card-body">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Filename</th>
                      <th>Description</th>
                      <th>Size</th>
                      {/* V4.9.6 — Uploaded by column. Shows uploader name +
                          alias so the team can trace who attached what. */}
                      <th>Uploaded by</th>
                      <th>Uploaded</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {ticket.attachments.map((att) => (
                      <tr key={att.id} className="no-hover">
                        <td>
                          <button
                            type="button"
                            className="btn-link"
                            onClick={() =>
                              attachmentsApi
                                .download(att.s3Key, att.filename)
                                .catch((err) =>
                                  push({
                                    kind: 'error',
                                    title: 'Download failed',
                                    message:
                                      err instanceof ApiError
                                        ? err.problem?.detail ?? err.message
                                        : 'Could not retrieve attachment.'
                                  })
                                )
                            }
                          >
                            {att.filename}
                          </button>
                        </td>
                        <td>{att.description ?? '—'}</td>
                        <td>{formatBytes(att.sizeBytes)}</td>
                        <td>
                          {att.uploaded_by ? (
                            <>
                              {att.uploaded_by.name}
                              <div className="text-small text-muted">
                                @{att.uploaded_by.alias}
                              </div>
                            </>
                          ) : (
                            // Fallback to UUID resolution via the users
                            // lookup if the join is missing (defensive —
                            // shouldn't happen post-V4.9.6).
                            aliasFor(att.uploadedById)
                          )}
                        </td>
                        <td>{formatDateTime(att.createdAt)}</td>
                        <td>
                          {/* V4.9.7 — View + Download buttons.
                              View opens an in-browser preview modal
                              (images, PDFs, text, video, audio). */}
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button
                              type="button"
                              className="btn btn-secondary btn-sm"
                              onClick={() =>
                                setPreviewAttachment({
                                  id: att.id,
                                  s3Key: att.s3Key,
                                  filename: att.filename,
                                  mimeType: att.mimeType,
                                  sizeBytes: att.sizeBytes
                                })
                              }
                            >
                              View
                            </button>
                            <button
                              type="button"
                              className="btn btn-secondary btn-sm"
                              onClick={() =>
                                attachmentsApi
                                  .download(att.s3Key, att.filename)
                                  .catch((err) =>
                                    push({
                                      kind: 'error',
                                      title: 'Download failed',
                                      message:
                                        err instanceof ApiError
                                          ? err.problem?.detail ?? err.message
                                          : 'Could not retrieve attachment.'
                                    })
                                  )
                              }
                            >
                              Download
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </div>

        <aside className="flex flex-col gap-4">
          {/* V4.9.13 — Status meta badges on the RIGHT (moved off the top
              header per ops). Severity, SLA/breach, CX (where relevant),
              approval state, and who last changed the status. */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Status</h3>
            </div>
            <div className="card-body">
              <div className="ticket-status-badges">
                <SeverityBadge severity={ticket.severity} />
                <BreachBadge
                  flag={ticket.breach_flag}
                  responsible={ticket.breach_responsible}
                  hoursOver={ticket.hrs_to_breach}
                  withResponsible={false}
                />
                {categoryUsesCx(ticket.category) ? <CxBadge status={ticket.cx_status} /> : null}
                {ticket.approval_state === ApprovalState.WaitingAdmin &&
                ticket.action_required_by?.role === 'approver' ? null : (
                  <ApprovalBadge state={ticket.approval_state} />
                )}
                {(() => {
                  const lastChange = ticket.timeline_events?.find((ev: any) => {
                    const t = ev.eventType ?? ev.event_type;
                    return t === 'status_change' || t === 'status_changed';
                  });
                  const actorName = lastChange
                    ? ((lastChange as any).actorName ?? (lastChange as any).actor_name ?? null)
                    : null;
                  if (!actorName) return null;
                  return (
                    <span
                      className="status-changed-by-pill"
                      title={`Status last changed by ${actorName}`}
                    >
                      <span className="status-changed-by-label">CHANGED BY</span>
                      <span className="status-changed-by-name">{actorName}</span>
                    </span>
                  );
                })()}
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Overview</h3>
            </div>
            <div className="card-body">
              <dl className="detail-meta">
                <div>
                  <dt>Category</dt>
                  <dd>{categoryLabel(ticket.category)}</dd>
                </div>
                {/* V4.9.13 — Show the B1 quotation sub-type (Standard / Bulk /
                    Trade show booth kit) so two Quotation tickets are no longer
                    indistinguishable. Only rendered when the ticket has one. */}
                {ticket.quotation_type ? (
                  <div>
                    <dt>Quotation type</dt>
                    <dd>{ticket.quotation_type}</dd>
                  </div>
                ) : null}
                <div>
                  <dt>Store</dt>
                  <dd>{storeLabel(ticket.store_code)}</dd>
                </div>
                <div>
                  <dt>Order #</dt>
                  <dd>{ticket.order_number ?? '—'}</dd>
                </div>
                <div>
                  <dt>Created</dt>
                  <dd>{formatDateTime(ticket.created_at)}</dd>
                </div>
                <div>
                  <dt>Due time</dt>
                  <dd>{formatDateTime(ticket.sla_deadline)}</dd>
                </div>
                <div>
                  <dt>Event date</dt>
                  <dd>{ticket.event_date ?? '—'}</dd>
                </div>
                <div>
                  <dt>Resolved</dt>
                  <dd>{ticket.resolved_at ? formatDateTime(ticket.resolved_at) : '—'}</dd>
                </div>
                <div>
                  <dt>Outcome</dt>
                  <dd>{ticket.outcome ?? '—'}</dd>
                </div>
              </dl>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Customer</h3>
            </div>
            <div className="card-body">
              <div className="kv-list">
                <div>
                  <span className="k">Name</span>
                  <span>{ticket.customer_name ?? '—'}</span>
                </div>
                <div>
                  <span className="k">Email</span>
                  <span>{ticket.customer_email ?? '—'}</span>
                </div>
                <div>
                  <span className="k">Mood</span>
                  <span>{ticket.customer_mood}</span>
                </div>
                <div>
                  <span className="k">Last CX informed</span>
                  <span>
                    {ticket.cx_last_informed_at ? formatRelative(ticket.cx_last_informed_at) : 'Never'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Team</h3>
            </div>
            <div className="card-body">
              <div className="kv-list">
                <div>
                  <span className="k">Owner</span>
                  <span>{ticket.owner ? ticket.owner.name : 'Unassigned'}</span>
                </div>
                <div>
                  <span className="k">Communicator</span>
                  <span>{ticket.communicator ? ticket.communicator.name : '—'}</span>
                </div>
                <div>
                  <span className="k">Investigator</span>
                  <span>{aliasFor(ticket.investigator_id)}</span>
                </div>
                <div>
                  <span className="k">Solver</span>
                  <span>{aliasFor(ticket.solver_id)}</span>
                </div>
                <div>
                  <span className="k">Raised by</span>
                  <span>{aliasFor(ticket.raised_by_id)}</span>
                </div>
              </div>
            </div>
          </div>

          {/*
            V4.9.10 — Fault attribution card. Admin-only. Setting this
            populates the Reports module's fault tracking + repeat
            offenders tables. Hidden for non-admin users so the UI
            mirrors backend access control (PATCH /tickets/:id/fault
            is admin-only).
          */}
          {isAdmin ? (
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Fault attribution</h3>
                <span className="text-muted text-small">
                  Admin only — tag who owns this ticket's failure (used in Reports).
                </span>
              </div>
              <div className="card-body">
                <select
                  className="form-select"
                  value={ticket.fault_attribution ?? ''}
                  disabled={faultMutation.isPending}
                  onChange={(e) => {
                    const v = e.target.value;
                    faultMutation.mutate(v === '' ? null : v);
                  }}
                >
                  <option value="">— Unattributed —</option>
                  {FAULT_ATTRIBUTION_VALUES.map((f) => (
                    <option key={f} value={f}>
                      {FAULT_ATTRIBUTION_LABELS[f]}
                    </option>
                  ))}
                </select>
                {ticket.fault_attribution ? (
                  <div className="text-small text-muted mt-2">
                    Current:{' '}
                    <strong>
                      {FAULT_ATTRIBUTION_LABELS[ticket.fault_attribution as FaultAttribution] ??
                        ticket.fault_attribution}
                    </strong>
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}

          {ticket.pending_approval ? (
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Pending approval</h3>
              </div>
              <div className="card-body">
                <div className="kv-list">
                  <div>
                    <span className="k">Requested by</span>
                    <span className="font-mono text-small">
                      {ticket.pending_approval.requestedById ?? '—'}
                    </span>
                  </div>
                  <div>
                    <span className="k">Requested at</span>
                    <span>{formatDateTime(ticket.pending_approval.createdAt)}</span>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </aside>
      </div>

      <ChangeStatusModal
        ticketId={ticketId}
        ticketCategory={ticket.category}
        currentStatus={ticket.status}
        open={showStatus}
        onClose={() => setShowStatus(false)}
      />
      <LogCxModal ticketId={ticketId} open={showCx} onClose={() => setShowCx(false)} />
      <CloseTicketModal ticketId={ticketId} open={showClose} onClose={() => setShowClose(false)} />
      <AddNoteModal ticketId={ticketId} open={showNote} onClose={() => setShowNote(false)} />
      <ResolutionModal
        ticketId={ticketId}
        open={showResolution}
        onClose={() => setShowResolution(false)}
        current={{
          issue_note: ticket.issue_note ?? null,
          cost_region: ticket.cost_region ?? null,
          cost_amount: ticket.cost_amount ?? null,
          payment_mode: ticket.payment_mode ?? null,
          remarks: ticket.remarks ?? null
        }}
      />
      <HandoffModal
        ticketId={ticketId}
        initialNote={ticket.handoff_note}
        open={showHandoff}
        onClose={() => setShowHandoff(false)}
      />
      <ReassignModal
        ticketId={ticketId}
        open={showReassign}
        onClose={() => setShowReassign(false)}
        currentOwnerId={ticket.owner_id}
        currentSolverId={ticket.solver_id}
        currentCommunicatorId={ticket.communicator_id}
        currentInvestigatorId={ticket.investigator_id}
      />
      <ApproveModal ticketId={ticketId} open={showApprove} onClose={() => setShowApprove(false)} />
      <IssueFaultModal
        ticketId={ticketId}
        open={showFault}
        onClose={() => setShowFault(false)}
        currentMarks={faultMarks}
        customerName={ticket.customer_name}
      />
      <ChangeCategoryModal
        ticketId={ticketId}
        currentCategory={ticket.category}
        open={showCategory}
        onClose={() => setShowCategory(false)}
      />
      {/* V4.9.7 — Attachment preview modal. Closes by clearing the
          previewAttachment state. */}
      <AttachmentPreviewModal
        open={previewAttachment !== null}
        attachment={previewAttachment}
        onClose={() => setPreviewAttachment(null)}
        onDownload={() => {
          if (!previewAttachment) return;
          attachmentsApi
            .download(previewAttachment.s3Key, previewAttachment.filename)
            .catch((err) =>
              push({
                kind: 'error',
                title: 'Download failed',
                message:
                  err instanceof ApiError
                    ? err.problem?.detail ?? err.message
                    : 'Could not retrieve attachment.'
              })
            );
        }}
      />
    </div>
  );
}
